import React, { useEffect, useState, useRef } from 'react';
import { Button, Form, Input, Modal, Table, Space, Row, Col, InputNumber, notification, Popconfirm, Tag, Tour, ConfigProvider, DatePicker, Image, Select } from 'antd';
import type { TourProps } from 'antd';
import { EditOutlined, PlusOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import viVN from 'antd/locale/vi_VN';
import dayjs from 'dayjs';
import useDrivePicker from 'react-google-drive-picker';
import { Brewer } from '../types/brewer';
import { BrewerService } from '../services/brewerService';

const { Search } = Input;

const brewerService = new BrewerService();

const BrewerManagement: React.FC = () => {
  const [brewers, setBrewers] = useState<Brewer[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingBrewer, setEditingBrewer] = useState<Brewer | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [filteredBrewers, setFilteredBrewers] = useState<Brewer[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imageFilesPreview, setImageFilesPreview] = useState<any[]>([]);
  const [driveImages, setDriveImages] = useState<{ fileId: string; webViewLink: string; fileName: string; }[]>([]);
  const [driveImagesPreview, setDriveImagesPreview] = useState<{ fileId: string; webViewLink: string; fileName: string; }[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitLoading, setSubmitLoading] = useState(false);

  const titleRef = useRef<HTMLDivElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<any>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const [openPicker] = useDrivePicker();



  useEffect(() => {
    fetchBrewers();
  }, []);

  useEffect(() => {
    const filtered = brewers.filter(brewer =>
      brewer.product_name.toLowerCase().includes(searchText.toLowerCase()) ||
      brewer.info.toLowerCase().includes(searchText.toLowerCase()) ||
      brewer.color.toLowerCase().includes(searchText.toLowerCase()) ||
      brewer.made_in.toLowerCase().includes(searchText.toLowerCase()) ||
      brewer.manufacture.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredBrewers(filtered);
  }, [brewers, searchText]);

  const fetchBrewers = async () => {
    setLoading(true);
    try {
      console.log('Fetching brewers...');
      const data = await brewerService.getAll();
      console.log('Brewers fetched:', data);
      setBrewers(data);
      setFilteredBrewers(data);
    } catch (error) {
      console.error('Error fetching brewers:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể tải danh sách máy pha cà phê',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingBrewer(null);
    form.resetFields();
    setImageFiles([]);
    setImageFilesPreview([]);
    setDriveImages([]);
    setDriveImagesPreview([]);
    setUploadProgress(0);
    setIsModalVisible(true);
  };

  const handleEdit = (brewer: Brewer) => {
    setEditingBrewer(brewer);

    // Reset image states
    setImageFiles([]);
    setDriveImages([]);
    setUploadProgress(0);

    // Set existing images for preview
    if (brewer.driveImages) {
      setDriveImagesPreview(brewer.driveImages.map(img => ({
        ...img,
        fileName: img.fileName || 'Untitled'
      })));
    } else {
      setDriveImagesPreview([]);
    }

    if (brewer.images) {
      setImageFilesPreview(brewer.images);
    } else {
      setImageFilesPreview([]);
    }

    // Set form data without date conversion since we removed date fields
    const formData = {
      ...brewer,
    };

    form.setFieldsValue(formData);
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await brewerService.delete(id);
      fetchBrewers(); // Refresh danh sách
      notification.success({
        message: 'Thành công',
        description: 'Đã xóa máy pha cà phê',
      });
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: 'Không thể xóa máy pha cà phê',
      });
    }
  };

  // Hàm xử lý chọn ảnh từ local
  const handleImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setImageFiles([...imageFiles, ...files]);
  };

  // Hàm xử lý chọn ảnh từ Google Drive
  const handleOpenPicker = () => {
    openPicker({
      clientId: "85231195954-hsetf3cpqpfcr67p2k40001og3054t52.apps.googleusercontent.com",
      developerKey: "AIzaSyD-xnVTuCx7ZDPzxrTmeL4my9FuIfkpYtY",
      viewId: "DOCS",
      showUploadView: true,
      showUploadFolders: true,
      supportDrives: true,
      multiselect: false,
      callbackFunction: (data) => {
        if (data.action === 'picked') {
          const file = data.docs[0];
          setDriveImages(prev => [...prev, {
            fileId: file.id,
            webViewLink: file.url || '',
            fileName: file.name
          }]);
        }
      },
    });
  };

  // Hàm xóa ảnh local
  const handleRemoveLocalImage = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Hàm xóa ảnh preview local
  const handleRemoveLocalPreviewImage = (index: number) => {
    setImageFilesPreview(prev => prev.filter((_, i) => i !== index));
  };

  // Hàm xóa ảnh Drive
  const handleRemoveDriveImage = (index: number) => {
    setDriveImages(prev => prev.filter((_, i) => i !== index));
  };

  // Hàm xóa ảnh preview Drive
  const handleRemoveDrivePreviewImage = (index: number) => {
    setDriveImagesPreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitLoading(true);
      setUploadProgress(0);

      const lstDriveImages: any[] = [];
      const newsLstImage: any[] = [];
      const oldLstImage: any[] = [];

      imageFiles.forEach(file => {
        newsLstImage.push(file);
      });

      imageFilesPreview?.forEach(file => {
        oldLstImage.push(file);
      });

      driveImages.forEach(image => {
        lstDriveImages.push({
          fileId: image.fileId,
          webViewLink: image.webViewLink,
          fileName: image.fileName
        });
      });

      driveImagesPreview?.forEach(image => {
        lstDriveImages.push({
          fileId: image.fileId,
          webViewLink: image.webViewLink,
          fileName: image.fileName
        });
      });

      // Hiển thị progress indicator nếu có ảnh cần upload
      if (newsLstImage.length > 0) {
        setUploadProgress(10);
        // Simulate progress
        const interval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev >= 90) {
              clearInterval(interval);
              return 90;
            }
            return prev + 5;
          });
        }, 1000);
      }

      // Set formatted values without date conversion since we removed date fields
      const formattedValues = {
        ...values,
        driveImages: lstDriveImages,
      };

      if (editingBrewer) {
        await brewerService.updateBrewer(
          editingBrewer.id!,
          formattedValues,
          newsLstImage,
          oldLstImage
        );
        notification.success({
          message: 'Thành công',
          description: 'Đã cập nhật máy pha cà phê',
        });
      } else {
        await brewerService.addBrewer(formattedValues, imageFiles);
        notification.success({
          message: 'Thành công',
          description: 'Đã thêm máy pha cà phê mới',
        });
      }

      setUploadProgress(100);
      setIsModalVisible(false);
      form.resetFields();
      setImageFiles([]);
      setImageFilesPreview([]);
      setDriveImages([]);
      setDriveImagesPreview([]);
      fetchBrewers(); // Refresh danh sách
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: 'Không thể lưu thông tin máy pha cà phê',
      });
    } finally {
      setSubmitLoading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'inactive': return 'red';
      case 'maintenance': return 'orange';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Hoạt động';
      case 'inactive': return 'Ngừng hoạt động';
      case 'maintenance': return 'Bảo trì';
      default: return status;
    }
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      render: (_: any, __: any, index: number) => index + 1
    },
    {
      title: 'Hình ảnh',
      dataIndex: 'images',
      key: 'images',
      width: 120,
      render: (images: string[], record: Brewer) => (
        <Image.PreviewGroup>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            maxWidth: 120,
          }}>
            {(record.driveImages && record.driveImages.length > 0) && record.driveImages.map((image, index) => (
              <div key={index} style={{ marginBottom: 8 }}>
                <Image
                  src={`https://lh3.googleusercontent.com/d/${image.fileId}?authuser=server`}
                  alt={image.fileName}
                  style={{ width: 50, height: 50, objectFit: 'cover' }}
                />
              </div>
            ))}

            {images && images.map((url, index) => (
              <div key={index} style={{ marginBottom: 8 }}>
                <Image
                  src={url}
                  alt={`brewer-${index}`}
                  width={50}
                  height={50}
                  style={{ objectFit: 'cover' }}
                />
              </div>
            ))}
          </div>
        </Image.PreviewGroup>
      )
    },
            {
            title: 'Tên sản phẩm',
            dataIndex: 'product_name',
            key: 'product_name',
            width: 250,
            sorter: (a: Brewer, b: Brewer) => a.product_name.localeCompare(b.product_name),
            ellipsis: true,
        },
    {
      title: 'Thông tin',
      dataIndex: 'info',
      key: 'info',
      width: 200,
      ellipsis: true,
      render: (info: string) => (
        <span title={info}>
          {info.length > 50 ? `${info.substring(0, 50)}...` : info}
        </span>
      )
    },
    {
      title: 'Khối lượng',
      dataIndex: 'weight',
      key: 'weight',
      width: 100,
    },
    {
      title: 'Thời gian nóng',
      dataIndex: 'heating_time',
      key: 'heating_time',
      width: 120,
    },
    {
      title: 'Nhiệt độ max',
      dataIndex: 'max_temperature',
      key: 'max_temperature',
      width: 120,
    },
    {
      title: 'Áp suất max',
      dataIndex: 'max_pressure',
      key: 'max_pressure',
      width: 120,
    },
    {
      title: 'Giới hạn SD',
      dataIndex: 'usage_limit',
      key: 'usage_limit',
      width: 150,
      ellipsis: true,
      render: (usage: string) => (
        <span title={usage}>
          {usage.length > 20 ? `${usage.substring(0, 20)}...` : usage}
        </span>
      )
    },
    {
      title: 'Dung lượng pin',
      dataIndex: 'battery_capacity',
      key: 'battery_capacity',
      width: 120,
    },
    {
      title: 'Phương pháp pha',
      dataIndex: 'brewing_method',
      key: 'brewing_method',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Dung tích nước',
      dataIndex: 'max_water_volume',
      key: 'max_water_volume',
      width: 120,
    },
    {
      title: 'Màu sắc',
      dataIndex: 'color',
      key: 'color',
      width: 150,
      ellipsis: true,
      render: (color: string) => (
        <span title={color}>
          {color.length > 20 ? `${color.substring(0, 20)}...` : color}
        </span>
      )
    },
    {
      title: 'Hãng SX',
      dataIndex: 'manufacture',
      key: 'manufacture',
      width: 100,
    },
    {
      title: 'Giá (VND)',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      sorter: (a: Brewer, b: Brewer) => a.price - b.price,
      render: (price: number) => `${price.toLocaleString('vi-VN')} ₫`,
    },
    {
      title: 'Xuất xứ',
      dataIndex: 'made_in',
      key: 'made_in',
      width: 100,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right' as const,
      width: 120,
      render: (_: any, record: Brewer) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Xóa máy pha cà phê"
            description="Bạn có chắc chắn muốn xóa máy pha này?"
            onConfirm={() => handleDelete(record.id)}
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const steps: TourProps['steps'] = [
    {
      title: 'Chào mừng đến với Quản lý máy pha cà phê!',
      description: 'Đây là nơi bạn có thể quản lý tất cả các máy pha cà phê trong cửa hàng.',
      target: () => titleRef.current!,
    },
    {
      title: 'Thêm máy pha mới',
      description: 'Nhấp vào nút này để thêm máy pha cà phê mới vào hệ thống.',
      target: () => addButtonRef.current!,
    },
    {
      title: 'Tìm kiếm máy pha',
      description: 'Sử dụng ô tìm kiếm này để tìm máy pha theo tên, thương hiệu hoặc model.',
      target: () => searchRef.current!,
    },
    {
      title: 'Danh sách máy pha',
      description: 'Bảng này hiển thị tất cả máy pha cà phê với thông tin chi tiết và các thao tác.',
      target: () => tableRef.current!,
    },
    {
      title: 'Kết thúc hướng dẫn!',
      description: 'Bây giờ bạn đã sẵn sàng quản lý máy pha cà phê một cách hiệu quả!',
      target: () => titleRef.current!,
    },
  ];

  return (
    <ConfigProvider locale={viVN}>
      <AdminLayout>
        <div className="admin-page-header">
          <div className="admin-page-title">
            <h1 ref={titleRef}>
              Quản lý máy pha cà phê
            </h1>
            <Tag color="blue">{brewers.length} máy pha</Tag>
            <Button
              type="primary"
              icon={<QuestionCircleOutlined />}
              onClick={() => setOpen(true)}
              style={{ marginLeft: 8 }}
            >
              Hướng dẫn
            </Button>
          </div>

          <div className="admin-page-actions">
            <div className="admin-search-filters">
              <div ref={searchRef}>
                <Search
                  placeholder="Tìm kiếm theo tên, thông tin, màu sắc, xuất xứ, nhà sản xuất..."
                  allowClear
                  style={{ width: 300 }}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
            </div>

            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                className="admin-button"
                ref={addButtonRef}
              >
                Thêm máy pha
              </Button>
            </Space>
          </div>
        </div>

        <div className="admin-table-container" ref={tableRef}>
          <Table
            className="admin-table"
            columns={columns}
            dataSource={filteredBrewers}
            rowKey="id"
            loading={loading}
            scroll={{ x: 2600 }}
            pagination={{
              responsive: true,
              defaultPageSize: 10,
              position: ['bottomRight'],
              total: filteredBrewers.length,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} của ${total} máy pha`,
            }}
          />
        </div>

        <Modal
          title={editingBrewer ? 'Sửa máy pha cà phê' : 'Thêm máy pha cà phê'}
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
          width={800}
          className="admin-modal"
          destroyOnClose={true}
        >
          <div className="admin-form-container">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
            >
              <Form.Item
                name="product_name"
                label="Tên sản phẩm"
                rules={[{ required: true, message: 'Vui lòng nhập tên sản phẩm' }]}
              >
                <Input placeholder="Ví dụ: Máy Pha Cà Phê Outin Nano Portable Espresso Machine" />
              </Form.Item>

              <Form.Item
                name="info"
                label="Thông tin chi tiết"
                rules={[{ required: true, message: 'Vui lòng nhập thông tin chi tiết' }]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder="Thông tin chi tiết về sản phẩm, tính năng, ưu điểm..."
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="weight"
                    label="Khối lượng (g)"
                    rules={[{ required: true, message: 'Vui lòng nhập khối lượng' }]}
                  >
                    <Input placeholder="Ví dụ: 670g" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="heating_time"
                    label="Thời gian làm nóng (s)"
                    rules={[{ required: true, message: 'Vui lòng nhập thời gian làm nóng' }]}
                  >
                    <Input placeholder="Ví dụ: 180s" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="max_temperature"
                    label="Nhiệt độ tối đa (°C)"
                    rules={[{ required: true, message: 'Vui lòng nhập nhiệt độ tối đa' }]}
                  >
                    <Input placeholder="Ví dụ: 96 celcius" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="max_pressure"
                    label="Áp suất tối đa (bar)"
                    rules={[{ required: true, message: 'Vui lòng nhập áp suất tối đa' }]}
                  >
                    <Input placeholder="Ví dụ: 20 bar" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="battery_capacity"
                    label="Dung lượng pin (mAh)"
                    rules={[{ required: true, message: 'Vui lòng nhập dung lượng pin' }]}
                  >
                    <Input placeholder="Ví dụ: 7500mah" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="max_water_volume"
                    label="Dung tích nước tối đa (ml)"
                    rules={[{ required: true, message: 'Vui lòng nhập dung tích nước tối đa' }]}
                  >
                    <Input placeholder="Ví dụ: 80ml" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="usage_limit"
                label="Giới hạn sử dụng (lần)"
                rules={[{ required: true, message: 'Vui lòng nhập giới hạn sử dụng' }]}
              >
                <Input placeholder="Ví dụ: cold water: 5 times, hot water: 100 times" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="brewing_method"
                    label="Phương pháp pha (phương pháp)"
                    rules={[{ required: true, message: 'Vui lòng nhập phương pháp pha' }]}
                  >
                    <Input placeholder="Ví dụ: capsule, ground coffee" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="manufacture"
                    label="Nhà sản xuất (hãng)"
                    rules={[{ required: true, message: 'Vui lòng nhập nhà sản xuất' }]}
                  >
                    <Input placeholder="Ví dụ: outln" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="color"
                label="Màu sắc (màu)"
                rules={[{ required: true, message: 'Vui lòng nhập màu sắc' }]}
              >
                <Input placeholder="Ví dụ: space grey, forest green, teal, pearl white, fuchsia pink" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="professional_standards"
                    label="Tiêu chuẩn chuyên nghiệp"
                    rules={[{ required: true, message: 'Vui lòng nhập tiêu chuẩn chuyên nghiệp' }]}
                  >
                    <Input placeholder="Ví dụ: European Certified Materials & Coffee Brewing Center Specifications" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="charging_options"
                    label="Tùy chọn sạc "
                    rules={[{ required: true, message: 'Vui lòng nhập tùy chọn sạc' }]}
                  >
                    <Input placeholder="Ví dụ: usb-c, 24V Car Charger and USB Charger( >10W)" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="special_features"
                    label="Tính năng đặc biệt"
                    rules={[{ required: true, message: 'Vui lòng nhập tính năng đặc biệt' }]}
                  >
                    <Input placeholder="Ví dụ: self heating, rich crema, quick heat up, ipx6 waterproof" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="made_in"
                    label="Xuất xứ"
                    rules={[{ required: true, message: 'Vui lòng nhập xuất xứ' }]}
                  >
                    <Input placeholder="Ví dụ: china" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="price"
                    label="Giá (VND)"
                    rules={[{ required: true, message: 'Vui lòng nhập giá' }]}
                  >
                    <InputNumber
                      min={0}
                      placeholder="3660000"
                      style={{ width: '100%' }}
                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="warranty"
                    label="Thời gian bảo hành"
                    rules={[{ required: true, message: 'Vui lòng nhập thời gian bảo hành' }]}
                  >
                    <Input placeholder="Ví dụ: 12 months" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="status"
                    label="Trạng thái"
                  >
                    <Select placeholder="Chọn trạng thái">
                      <Select.Option value="active">Hoạt động</Select.Option>
                      <Select.Option value="inactive">Ngừng hoạt động</Select.Option>
                      <Select.Option value="maintenance">Bảo trì</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* Upload hình ảnh */}
              <Form.Item label="Hình ảnh sản phẩm">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <Space>
                    <Button onClick={() => document.getElementById('brewerFileInput')?.click()}>
                      Chọn ảnh từ local
                    </Button>
                    <Button onClick={handleOpenPicker}>
                      Chọn ảnh từ Google Drive
                    </Button>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImagesChange}
                      id="brewerFileInput"
                      style={{ display: 'none' }}
                    />
                  </Space>

                  {/* Upload progress indicator */}
                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div style={{ marginTop: 8 }}>
                      <div>Đang tải ảnh lên: {uploadProgress}%</div>
                      <div style={{ width: '100%', backgroundColor: '#f0f0f0', borderRadius: 4 }}>
                        <div
                          style={{
                            width: `${uploadProgress}%`,
                            backgroundColor: '#1890ff',
                            height: 8,
                            borderRadius: 4,
                            transition: 'width 0.3s ease'
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Local Files Preview */}
                  {imageFiles.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div>Ảnh từ local:</div>
                      <Space wrap>
                        {imageFiles.map((file, index) => (
                          <Tag
                            key={index}
                            color="blue"
                            closable
                            onClose={() => handleRemoveLocalImage(index)}
                            style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
                          >
                            <Space>
                              File local
                              <Image
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                style={{ width: 30, height: 30, objectFit: 'cover' }}
                              />
                            </Space>
                          </Tag>
                        ))}
                      </Space>
                    </div>
                  )}

                  {/* Drive Images Preview */}
                  {driveImages.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div>Ảnh từ Drive:</div>
                      <Space wrap>
                        {driveImages.map((image, index) => (
                          <Tag
                            key={index}
                            color="green"
                            closable
                            onClose={() => handleRemoveDriveImage(index)}
                            style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
                          >
                            <Space>
                              Google Drive
                              <Image
                                src={`https://lh3.googleusercontent.com/d/${image.fileId}?authuser=server`}
                                alt={image.fileName}
                                style={{ width: 30, height: 30, objectFit: 'cover' }}
                              />
                            </Space>
                          </Tag>
                        ))}
                      </Space>
                    </div>
                  )}

                  {/* Existing Images Preview */}
                  {((imageFilesPreview && imageFilesPreview.length > 0) ||
                    (driveImagesPreview && driveImagesPreview.length > 0)) && (
                      <div>
                        <div>Ảnh hiện tại:</div>

                        {imageFilesPreview && imageFilesPreview.length > 0 && (
                          <Space wrap>
                            {imageFilesPreview.map((file, index) => (
                              <Tag
                                key={index}
                                color="orange"
                                closable
                                onClose={() => handleRemoveLocalPreviewImage(index)}
                                style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
                              >
                                <Space>
                                  File hiện tại
                                  <Image
                                    src={file}
                                    alt={`file-${index}`}
                                    style={{ width: 30, height: 30, objectFit: 'cover' }}
                                  />
                                </Space>
                              </Tag>
                            ))}
                          </Space>
                        )}

                        {driveImagesPreview && driveImagesPreview.length > 0 && (
                          <Space wrap>
                            {driveImagesPreview.map((image, index) => (
                              <Tag
                                key={index}
                                color="purple"
                                closable
                                onClose={() => handleRemoveDrivePreviewImage(index)}
                                style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
                              >
                                <Space>
                                  Drive hiện tại
                                  <Image
                                    src={`https://lh3.googleusercontent.com/d/${image.fileId}?authuser=server`}
                                    alt={image.fileName}
                                    style={{ width: 30, height: 30, objectFit: 'cover' }}
                                  />
                                </Space>
                              </Tag>
                            ))}
                          </Space>
                        )}
                      </div>
                    )}
                </div>
              </Form.Item>



              <Form.Item
                name="notes"
                label="Ghi chú"
              >
                <Input.TextArea
                  rows={3}
                  placeholder="Nhập ghi chú về máy pha..."
                />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="admin-button"
                    loading={submitLoading}
                    disabled={submitLoading}
                  >
                    {editingBrewer ? 'Cập nhật' : 'Thêm mới'}
                  </Button>
                  <Button
                    onClick={() => setIsModalVisible(false)}
                    disabled={submitLoading}
                  >
                    Hủy
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        </Modal>

        <Tour open={open} onClose={() => setOpen(false)} steps={steps} />
      </AdminLayout>
    </ConfigProvider>
  );
};

export default BrewerManagement;